var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ThemeSelector_1;
import React, { Component } from 'react';
import { ThemeColor, ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, expression, listOf, oneOf, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { ThemeService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isFunction } from '@philips-internal/oswald-ui-utils';
import { ColorBlock } from './color-block.js';
import { ThemeSelectorAPI } from './theme-selector-api.js';
import './theme-selector.css';
var ItemName;
(function (ItemName) {
    ItemName["ACCENT_COLOR"] = "accentColor";
    ItemName["COLOR"] = "color";
    ItemName["CONTENT_TONAL_RANGE"] = "contentTonalRange";
    ItemName["NAVIGATION_TONAL_RANGE"] = "navigationTonalRange";
})(ItemName || (ItemName = {}));
const tonalRanges = [
    ThemeTonalRange.ULTRA_LIGHT,
    ThemeTonalRange.VERY_LIGHT,
    ThemeTonalRange.BRIGHT,
    ThemeTonalRange.VERY_DARK,
];
let ThemeSelector = ThemeSelector_1 = class ThemeSelector extends Component {
    onItemSelected(item, itemValue) {
        const { onChange, value } = this.props;
        if (!isFunction(onChange)) {
            return;
        }
        const newValue = {
            ...value,
            [item]: itemValue,
        };
        if (newValue.color && newValue.accentColor) {
            const supportedAccents = ThemeService.getSupportedAccents(newValue.color);
            if (!supportedAccents.includes(newValue.accentColor)) {
                delete newValue.accentColor;
            }
        }
        onChange(new ChangeEvent({
            oldValue: value,
            value: newValue,
        }));
    }
    getColorBlocks() {
        const { accentColorsExpression = ThemeSelector_1.defaultProps.accentColorsExpression, colorsExpression = ThemeSelector_1.defaultProps.colorsExpression, contentToneExpression = ThemeSelector_1.defaultProps.contentToneExpression, defaultColor = ThemeSelector_1.defaultProps.defaultColor, hiddenAccentColors = ThemeSelector_1.defaultProps.hiddenAccentColors, hiddenColors = ThemeSelector_1.defaultProps.hiddenColors, hiddenContentTotalRanges = ThemeSelector_1.defaultProps.hiddenContentTotalRanges, hiddenNavigationTotalRanges = ThemeSelector_1.defaultProps.hiddenNavigationTotalRanges, navigationToneExpression = ThemeSelector_1.defaultProps.navigationToneExpression, showAccentColor = ThemeSelector_1.defaultProps.showAccentColor, showColor = ThemeSelector_1.defaultProps.showColor, showContentTonalRange = ThemeSelector_1.defaultProps.showContentTonalRange, showNavigationTonalRange = ThemeSelector_1.defaultProps.showNavigationTonalRange, value, } = this.props;
        const currentColor = value?.color ?? defaultColor;
        const tonalRangeClassName = classNames({
            'c-theme-selector__tonal-range': true,
            [`t-color-${currentColor}`]: true,
        });
        const colorBlocks = [];
        if (showColor) {
            const colors = enumValues(ThemeColor);
            const showingColors = colors.filter(color => !hiddenColors.includes(color));
            colorBlocks.push(React.createElement(ColorBlock, { className: "c-theme-selector__color-range", colors: showingColors, key: ItemName.COLOR, onClick: (value) => this.onItemSelected(ItemName.COLOR, value), prefix: "t-color", selected: value?.color, title: colorsExpression }));
        }
        if (showAccentColor) {
            const accentColors = ThemeService.getSupportedAccents(value?.color ?? defaultColor);
            const showingAccentColors = accentColors.filter(color => !hiddenAccentColors.includes(color));
            colorBlocks.push(React.createElement(ColorBlock, { className: "c-theme-selector__accent-color-range", colors: showingAccentColors, key: ItemName.ACCENT_COLOR, onClick: (value) => this.onItemSelected(ItemName.ACCENT_COLOR, value), prefix: "t-color-accent", selected: value?.accentColor, title: accentColorsExpression }));
        }
        if (showContentTonalRange) {
            const showingContentTonalRanges = tonalRanges.filter(color => !hiddenContentTotalRanges.includes(color));
            colorBlocks.push(React.createElement(ColorBlock, { className: tonalRangeClassName, colors: showingContentTonalRanges, key: ItemName.CONTENT_TONAL_RANGE, onClick: (value) => this.onItemSelected(ItemName.CONTENT_TONAL_RANGE, value), prefix: "t-content", selected: value?.contentTonalRange, title: contentToneExpression }));
        }
        if (showNavigationTonalRange) {
            const showingNavigationTonalRanges = tonalRanges.filter(color => !hiddenNavigationTotalRanges.includes(color));
            colorBlocks.push(React.createElement(ColorBlock, { className: tonalRangeClassName, colors: showingNavigationTonalRanges, key: ItemName.NAVIGATION_TONAL_RANGE, onClick: (value) => this.onItemSelected(ItemName.NAVIGATION_TONAL_RANGE, value), prefix: "t-nav", selected: value?.navigationTonalRange, title: navigationToneExpression }));
        }
        return colorBlocks;
    }
    render() {
        const { className, enabled = ThemeSelector_1.defaultProps.enabled } = this.props;
        const themeSelectorClassName = classNames({
            'c-theme-selector': true,
            'c-theme-selector--disabled': !enabled,
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: themeSelectorClassName }, this.getColorBlocks()));
    }
};
ThemeSelector.propTypes = {
    accentColorsExpression: expression,
    className: string,
    colorsExpression: expression,
    contentToneExpression: expression,
    defaultColor: oneOf(enumValues(ThemeColor)),
    enabled: bool,
    hiddenAccentColors: listOf(oneOf(enumValues(ThemeColor))),
    hiddenColors: listOf(oneOf(enumValues(ThemeColor))),
    hiddenContentTotalRanges: listOf(oneOf(tonalRanges)),
    hiddenNavigationTotalRanges: listOf(oneOf(tonalRanges)),
    navigationToneExpression: expression,
    onChange: eventListener,
    showAccentColor: bool,
    showColor: bool,
    showContentTonalRange: bool,
    showNavigationTonalRange: bool,
    value: shape({
        accentColor: oneOf(enumValues(ThemeColor)),
        color: oneOf(enumValues(ThemeColor)),
        contentTonalRange: oneOf(enumValues(ThemeTonalRange)),
        navigationTonalRange: oneOf(enumValues(ThemeTonalRange)),
    }).notEnumerable(),
};
ThemeSelector.defaultProps = {
    accentColorsExpression: 'tws.theme-selector.accent-colors',
    colorsExpression: 'tws.theme-selector.colors',
    contentToneExpression: 'tws.theme-selector.content-tone',
    defaultColor: ThemeColor.BLUE,
    enabled: true,
    hiddenAccentColors: [],
    hiddenColors: [],
    hiddenContentTotalRanges: [],
    hiddenNavigationTotalRanges: [],
    navigationToneExpression: 'tws.theme-selector.navigation-tone',
    showAccentColor: true,
    showColor: true,
    showContentTonalRange: true,
    showNavigationTonalRange: true,
};
ThemeSelector = ThemeSelector_1 = __decorate([
    AwesomeComponent({ api: ThemeSelectorAPI, type: ComponentType.DATA_ENTRY })
], ThemeSelector);
export { ThemeSelector, };
//# sourceMappingURL=theme-selector.js.map